import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import type DataMapperViewStore from "./DataMapperViewStore";

/** The Data Mapper ServiceStore paths (serviceStores/dataMapper.stores.ts). */
export type DataMapperServicePath =
  | "data-mapper-profiles"
  | "data-mapper-schemas"
  | "data-mapper-lookup-tables"
  | "data-mapper-source-configs"
  | "data-mapper-mapping-configs"
  | "data-mapper-target-connections";

const ROOT_KEYS: Record<DataMapperServicePath, string> = {
  "data-mapper-profiles": "dataMapperProfilesStore",
  "data-mapper-schemas": "dataMapperSchemasStore",
  "data-mapper-lookup-tables": "dataMapperLookupTablesStore",
  "data-mapper-source-configs": "dataMapperSourceConfigsStore",
  "data-mapper-mapping-configs": "dataMapperMappingConfigsStore",
  "data-mapper-target-connections": "dataMapperTargetConnectionsStore",
};

/** The data-plane service paths: called through the raw client, never a ServiceStore. */
export type DataMapperDataPlanePath =
  | "data-mapper-data-source"
  | "data-mapper-source-tables"
  | "data-mapper-source-data"
  | "data-mapper-target-connection"
  | "data-mapper-imports"
  | "data-mapper-explorer-schema"
  | "data-mapper-explorer-profiler"
  | "data-mapper-explorer-analysis"
  | "data-mapper-restore"
  // AI suggestions (Map and Transform): answers with no ids, and nothing worth caching.
  | "data-mapper-llm-mapping"
  // The HQ Connector: the person's own connector's status and the downloads (find only).
  | "data-mapper-connector"
  // Titan Bulk Export: a create({ action }) dispatcher, get / remove by export id, and its
  // per-tab events; the download is a fetch with the person's own session.
  | "data-mapper-bulk-export"
  // Not the data plane, but called the same way: its find answers reports
  // GROUPED by database (no ids at the top level) and its get carries the whole
  // report, neither of which a ServiceStore cache can hold (Explorer store).
  | "data-mapper-explorer-reports";

/** The slice of a raw Feathers client service the data-plane screens call. */
export interface DataPlaneService {
  find: (params?: { query?: Record<string, unknown> }) => Promise<unknown>;
  get: (id: string, params?: { query?: Record<string, unknown> }) => Promise<unknown>;
  create: (data: Record<string, unknown>, params?: object) => Promise<unknown>;
  /** `data-mapper-imports` only (cancel). */
  patch?: (id: string, data: Record<string, unknown>, params?: object) => Promise<unknown>;
  remove?: (id: string, params?: { query?: Record<string, unknown> }) => Promise<unknown>;
}

/**
 * A data-plane service on the RAW Feathers client (InstanceUsersStore's
 * pattern): it rejects on every refusal, caches nothing, and keeps no copy of
 * a request body — which is what the connect call's password needs.
 */
export function dataPlaneService(rootStore: RootStore, path: DataMapperDataPlanePath): DataPlaneService | null {
  const app = (rootStore as unknown as { app?: { service: (p: string) => DataPlaneService } }).app;
  return app?.service(path) ?? null;
}

/** The slice of the raw `data-mapper-source-configs` client service a secret-bearing save calls. */
export interface RawSourceConfigsService {
  create: (data: Record<string, unknown>, params?: object) => Promise<unknown>;
  patch: (id: string, data: Record<string, unknown>, params?: object) => Promise<unknown>;
}

/**
 * `data-mapper-source-configs` on the RAW client, for a save whose body carries
 * secrets (a REST source's token or headers) so the API can report what it
 * dropped: unlike its ServiceStore, the raw client is no MobX action, keeps no
 * copy of the body, and rejects on every refusal.
 */
export function rawSourceConfigsService(rootStore: RootStore): RawSourceConfigsService | null {
  const app = (rootStore as unknown as { app?: { service: (p: string) => RawSourceConfigsService } }).app;
  return app?.service("data-mapper-source-configs") ?? null;
}

/** A registered Data Mapper ServiceStore, by path (the same lookup DevOpsConnectionsStore uses). */
export function dataMapperService(
  rootStore: RootStore,
  path: DataMapperServicePath
): ServiceStoreType | null {
  const root = rootStore as unknown as {
    serviceStoreManager?: { getStore: (p: string) => unknown } | null;
  } & Record<string, unknown>;
  return ((root.serviceStoreManager?.getStore(path) ?? root[ROOT_KEYS[path]]) as ServiceStoreType | null) ?? null;
}

/** The tab's selection store (registered as `dataMapperViewStore`). */
export function dataMapperSelection(rootStore: RootStore): DataMapperViewStore | null {
  return ((rootStore as unknown as { dataMapperViewStore?: DataMapperViewStore }).dataMapperViewStore ?? null);
}
